import { useState } from "react";
import { toast } from "sonner";
import { Signature, X, ArrowUUpLeft } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import SignaturePad from "@/components/SignaturePad";

/**
 * QuickSignButton — abre un mini-dialog optimizado para móvil que permite
 * al supervisor firmar el Reporte Diario directamente desde la lista sin
 * abrir el detalle. Llama PATCH /hse/daily-reports/{id}/quick-sign.
 */
export default function QuickSignButton({ report, onSigned }) {
  const [open, setOpen] = useState(false);
  const [sig, setSig] = useState("");
  const [saving, setSaving] = useState(false);
  const hasSig = !!report.supervisor_signature;

  const submit = async () => {
    if (!sig || !sig.startsWith("data:image/")) {
      toast.error("Dibuja tu firma antes de guardar");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/hse/daily-reports/${report.id}/quick-sign`, {
        supervisor_signature: sig,
      });
      toast.success("Firma registrada ✓");
      setOpen(false);
      setSig("");
      onSigned?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSig(""); }}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          data-testid={`daily-quicksign-${report.id}`}
          className={`h-7 text-xs gap-1 ${hasSig ? "bg-green-50 border-green-300 text-green-700 hover:bg-green-100" : "bg-yellow-50 border-yellow-300 text-yellow-800 hover:bg-yellow-100"}`}
          title={hasSig ? "Ya firmado · Re-firmar" : "Firmar rápido"}
        >
          <Signature size={12} weight="bold" /> {hasSig ? "Re-firmar" : "Firmar"}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-white max-w-md p-0 gap-0"
        data-testid={`daily-quicksign-dialog-${report.id}`}
      >
        <DialogHeader className="p-4 border-b bg-orange-50">
          <DialogTitle className="font-heading text-base flex items-center gap-2">
            <Signature weight="bold" className="text-orange-700" />
            Firma rápida — {report.date} · {report.shift}
          </DialogTitle>
          <p className="text-xs text-slate-600 mt-1">Supervisor: <b>{report.supervisor || "-"}</b></p>
        </DialogHeader>

        <div className="p-4 space-y-3">
          <div className="border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 p-2 flex justify-center">
            <SignaturePad
              value={sig}
              onChange={setSig}
              width={window.innerWidth < 500 ? 280 : 380}
              height={140}
              testid={`quicksign-pad-${report.id}`}
            />
          </div>
          <p className="text-[11px] text-slate-500 text-center italic">
            Dibuja tu firma con el dedo o el mouse. Al guardar, se registra fecha, hora y
            usuario que firma para auditoría.
          </p>
          {hasSig && (
            <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1.5 text-[11px] text-blue-800 flex items-center justify-between gap-2">
              <span>⚠ Reporte ya firmado. Re-firmar reemplaza la firma anterior.</span>
              <button
                data-testid={`daily-quicksign-undo-${report.id}`}
                type="button"
                onClick={async () => {
                  if (!window.confirm("¿Deshacer la firma actual? Queda registrada en historial de auditoría.")) return;
                  try {
                    await api.delete(`/hse/daily-reports/${report.id}/quick-sign`);
                    toast.success("Firma deshecha · registrada en historial");
                    setOpen(false); setSig("");
                    onSigned?.({ ...report, supervisor_signature: "" });
                  } catch (e) { toast.error(apiError(e)); }
                }}
                className="text-blue-900 underline text-[10px] font-bold whitespace-nowrap inline-flex items-center gap-1"
              >
                <ArrowUUpLeft size={11} weight="bold" /> Deshacer firma
              </button>
            </div>
          )}
        </div>

        <div className="p-3 border-t bg-slate-50 flex gap-2 justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            disabled={saving}
            className="gap-1"
          >
            <X size={12} /> Cancelar
          </Button>
          <Button
            data-testid={`daily-quicksign-save-${report.id}`}
            onClick={submit}
            disabled={saving || !sig}
            className="bg-orange-700 hover:bg-orange-800 gap-1"
          >
            <Signature size={12} weight="bold" /> {saving ? "Guardando…" : "Guardar firma"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
